import React, { useEffect, useState } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { Landmark, Bell, Menu, X, Globe, LogOut, ShieldCheck, Users, Home as HomeIcon, UserCircle } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Layout() {
  const { t, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    api.get("/notifications").then(({ data }) => setUnread(data.filter((n) => !n.read).length)).catch(() => {});
  }, [user, loc.pathname]);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  const doLogout = async () => { await logout(); nav("/"); };
  const install = async () => { if (!deferredPrompt) return; deferredPrompt.prompt(); setDeferredPrompt(null); };

  const links = [
    { to: "/", label: t("nav.home"), icon: HomeIcon, show: true },
    { to: "/countries", label: t("nav.countries"), icon: Globe, show: true },
    { to: "/registry", label: t("nav.registry"), icon: ShieldCheck, show: true },
    { to: "/committee", label: t("nav.committee"), icon: Users, show: user && (user.role === "committee_member" || user.role === "admin") },
    { to: "/admin", label: t("nav.admin"), icon: Landmark, show: user && user.role === "admin" },
  ].filter((l) => l.show);

  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="sticky top-0 z-50 border-b border-border bg-white/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
            <div className="h-9 w-9 rounded-md bg-primary flex items-center justify-center">
              <Landmark className="h-5 w-5 text-primary-foreground" />
            </div>
            <span className="font-heading font-black text-xl tracking-tighter">Agorà</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.to.replace(/\//g, "") || "home"}`}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive(l.to) ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-secondary"}`}>
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center rounded-md border border-border overflow-hidden text-xs font-semibold">
              {["it", "en"].map((l) => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
                  className={`px-2.5 py-1.5 uppercase transition-colors ${lang === l ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                  {l}
                </button>
              ))}
            </div>
            {user && (
              <Link to="/profile" data-testid="notif-bell" className="relative p-2 rounded-md hover:bg-secondary">
                <Bell className="h-5 w-5" />
                {unread > 0 && <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">{unread}</span>}
              </Link>
            )}
            {user ? (
              <>
                <Link to="/profile" data-testid="nav-profile" className="hidden md:inline-flex p-2 rounded-md hover:bg-secondary"><UserCircle className="h-5 w-5" /></Link>
                <Button data-testid="logout-btn" onClick={doLogout} variant="outline" size="sm" className="hidden md:inline-flex"><LogOut className="h-4 w-4 mr-1" />{t("nav.logout")}</Button>
              </>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Button data-testid="header-login-btn" onClick={() => nav("/login")} variant="ghost" size="sm">{t("nav.login")}</Button>
                <Button data-testid="header-register-btn" onClick={() => nav("/register")} size="sm">{t("nav.register")}</Button>
              </div>
            )}
            <button data-testid="mobile-menu-btn" className="md:hidden p-2 rounded-md hover:bg-secondary" onClick={() => setOpen(!open)}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-border bg-white px-4 py-3 space-y-1">
            {links.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`mnav-${l.to.replace(/\//g, "") || "home"}`}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-medium ${isActive(l.to) ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>
                <l.icon className="h-4 w-4" />{l.label}
              </Link>
            ))}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center rounded-md border border-border overflow-hidden text-xs font-semibold">
                {["it", "en"].map((l) => (
                  <button key={l} data-testid={`mlang-${l}`} onClick={() => setLang(l)} className={`px-3 py-1.5 uppercase ${lang === l ? "bg-primary text-primary-foreground" : ""}`}>{l}</button>
                ))}
              </div>
              {user ? (
                <><Link to="/profile" data-testid="mnav-profile" className="text-sm font-medium px-3 py-2">{t("nav.profile")}</Link>
                <Button data-testid="mlogout-btn" onClick={doLogout} variant="outline" size="sm">{t("nav.logout")}</Button></>
              ) : (
                <div className="flex gap-2">
                  <Button data-testid="mlogin-btn" onClick={() => nav("/login")} variant="ghost" size="sm">{t("nav.login")}</Button>
                  <Button data-testid="mregister-btn" onClick={() => nav("/register")} size="sm">{t("nav.register")}</Button>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {deferredPrompt && (
        <div className="bg-primary text-primary-foreground text-sm px-4 py-2 flex items-center justify-center gap-3">
          <span>{t("install.text")}</span>
          <button data-testid="install-btn" onClick={install} className="underline font-semibold">{t("install.btn")}</button>
        </div>
      )}

      <main className="flex-1 w-full">
        <Outlet />
      </main>

      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 font-heading font-bold text-foreground"><Landmark className="h-4 w-4 text-primary" />Agorà</div>
          <p>{t("app.tagline")} · {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
}
